import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/toaster";
import Auth from "./pages/Auth";
import Landing from "./pages/Landing";
import CustomerDashboard from "./pages/CustomerDashboard";
import ServiceRequest from "./pages/ServiceRequest";
import ServiceTracking from "./pages/ServiceTracking";
import Payment from "./pages/Payment";
import ServiceHistory from "./pages/ServiceHistory";
import DriverDashboard from "./pages/DriverDashboard";
import MyGarage from "./pages/MyGarage";
import DriverMembership from "./pages/DriverMembership";
import DriverPayouts from "./pages/DriverPayouts";
import AdminDashboard from "./pages/AdminDashboard";
import AdminContentEditor from "./pages/AdminContentEditor";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import QuoteViewer from "./pages/QuoteViewer";

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-wheely-navy-950 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-wheely-orange border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    if (user.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
    return <Navigate to={user.role === 'driver' ? '/driver/dashboard' : '/customer/dashboard'} replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Auth />} />

            {/* Customer Routes */}
            <Route
              path="/customer/dashboard"
              element={
                <ProtectedRoute requiredRole="customer">
                  <CustomerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer/request"
              element={
                <ProtectedRoute requiredRole="customer">
                  <ServiceRequest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer/tracking"
              element={
                <ProtectedRoute requiredRole="customer">
                  <ServiceTracking />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer/payment"
              element={
                <ProtectedRoute requiredRole="customer">
                  <Payment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer/history"
              element={
                <ProtectedRoute requiredRole="customer">
                  <ServiceHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer/garage"
              element={
                <ProtectedRoute requiredRole="customer">
                  <MyGarage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer/quotes"
              element={
                <ProtectedRoute requiredRole="customer">
                  <QuoteViewer />
                </ProtectedRoute>
              }
            />

            {/* Driver Routes */}
            <Route
              path="/driver/dashboard"
              element={
                <ProtectedRoute requiredRole="driver">
                  <DriverDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/membership"
              element={
                <ProtectedRoute requiredRole="driver">
                  <DriverMembership />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/payouts"
              element={
                <ProtectedRoute requiredRole="driver">
                  <DriverPayouts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/content"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminContentEditor />
                </ProtectedRoute>
              }
            />

            {/* Public Pages */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthProvider>
  );
}

export default App;
